import { Component, OnInit } from '@angular/core';
import { ROUTES } from 'src/app/constants/Routes';


@Component({
  selector: 'app-context-based-messaging',
  templateUrl: './context-based-messaging.component.html',
  styleUrls: ['./context-based-messaging.component.scss'],
  standalone: false
})
export class ContextBasedMessagingComponent implements OnInit {
  Routes = ROUTES;
  activeSection = 'overview';

  sections = [
    { id: 'overview', label: 'Overview', icon: 'bx-layer' },
    { id: 'capabilities', label: 'Capabilities', icon: 'bx-chip' },
    { id: 'pipeline', label: 'How It Works', icon: 'bx-git-branch' },
    { id: 'actions', label: 'Actions', icon: 'bx-broadcast' },
  ];

  highlights = [
    'Ingests telemetry, behavioural, geospatial, and system event data.',
    'Performs real-time context evaluation using configurable rule sets.',
    'Executes automated, event-driven message triggering workflows.',
    'Supports multi-channel delivery orchestration including Push, SMS, and Email.',
    'Enables high-throughput, low-latency communication at scale.',
  ];

  capabilities = [
    {
      icon: 'bx-data',
      title: 'Real-Time Data Ingestion',
      desc: 'Continuously consumes telemetry, behavioral, geospatial, system, and integration data from connected mobility ecosystems.'
    },
    {
      icon: 'bx-pulse',
      title: 'Context Evaluation Engine',
      desc: 'Evaluates live events against configurable business rules to determine whether communication should be triggered.'
    },
    {
      icon: 'bx-group',
      title: 'Dynamic Segmentation',
      desc: 'Builds audience buckets in real time or batch mode so only relevant users or devices are targeted.'
    },
    {
      icon: 'bx-message-square-detail',
      title: 'Multi-Channel Orchestration',
      desc: 'Routes communication through Push, SMS, Email, API triggers, and automation workflows from one decision layer.'
    },
  ];

  pipelineSteps = [
    {
      title: 'Data Ingestion & Data Lake',
      icon: 'bx-cloud-upload',
      points: [
        'Continuously ingests vehicle telemetry, user behavior, location, trip, system, and integration data.',
        'Stores all streams in a centralized data lake as a single source of truth.',
        'Supports both large-scale historical analysis and real-time processing readiness.',
      ]
    },
    {
      title: 'Event Detection & Trigger Engine',
      icon: 'bx-radar',
      points: [
        'Detects trigger conditions such as low battery, overspeeding, or service due events.',
        'Supports threshold-based, rule-based, and event-based trigger logic.',
        'Correlates multiple parameters such as time, location, and driving behavior before generating downstream events.',
      ]
    },
    {
      title: 'Audience Segmentation',
      icon: 'bx-user-pin',
      points: [
        'Identifies users or devices matching the defined trigger conditions.',
        'Builds dynamic buckets such as users with fuel below a threshold or vehicles entering a geofence.',
        'Supports real-time as well as batch segmentation for campaign-level targeting and prioritization.',
      ]
    },
    {
      title: 'Rule Engine & Decision Layer',
      icon: 'bx-cog',
      points: [
        'Defines message conditions, priorities, and frequency limits.',
        'Applies deduplication and cooldown logic to prevent notification fatigue.',
        'Determines the right action for each trigger based on configured orchestration rules.',
      ]
    },
    {
      title: 'Action & Orchestration Layer',
      icon: 'bx-share-alt',
      points: [
        'Executes Push, SMS, Email, API, or workflow automation actions.',
        'Supports service booking, alerts, rewards, and downstream system triggers.',
        'Extends beyond messaging into proactive orchestration across connected services.',
      ]
    },
  ];

  actionChannels = [
    {
      icon: 'bx-bell',
      title: 'Push Notifications',
      desc: 'Send mobile or in-app notifications for safety, alerts, service reminders, and contextual engagement moments.'
    },
    {
      icon: 'bx-message-dots',
      title: 'SMS & Email',
      desc: 'Deliver fallback or campaign communication through direct messaging channels when broader reach is needed.'
    },
    {
      icon: 'bx-link-external',
      title: 'API Triggers',
      desc: 'Trigger external systems and enterprise workflows from the same context-aware decision layer.'
    },
    {
      icon: 'bx-git-merge',
      title: 'Workflow Automation',
      desc: 'Coordinate service booking, reward flows, safety escalation, and support orchestration from live events.'
    },
  ];

  ngOnInit() {}


  scrollTo(id: string) {
    this.activeSection = id;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
